import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { toast } from 'sonner'
import { useLockedFieldHint } from '../../contexts/LockedFieldHintContext'

interface ComboboxProps {
  value: string
  options: string[]
  onChange: (value: string) => void
  /** Called when the user confirms a new value via “Use …”. */
  onCreateOption?: (value: string) => void | Promise<void>
  placeholder?: string
  allowCreate?: boolean
  className?: string
  inputClassName?: string
  interactionLocked?: boolean
  lockedHint?: string
}

export default function Combobox({
  value,
  options,
  onChange,
  onCreateOption,
  placeholder,
  allowCreate = true,
  className,
  inputClassName,
  interactionLocked,
  lockedHint
}: ComboboxProps) {
  const { showAtAnchor } = useLockedFieldHint()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredOptions = useMemo(() => {
    const query = value.trim().toLowerCase()
    if (!query || options.some((option) => option.toLowerCase() === query)) {
      return options
    }
    return options.filter((option) => option.toLowerCase().includes(query))
  }, [options, value])

  const hasExactMatch = options.some((option) => option.toLowerCase() === value.trim().toLowerCase())
  const showCreate = allowCreate && value.trim().length > 0 && !hasExactMatch

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      <div className="relative">
        <input
          readOnly={interactionLocked}
          className={clsx('input-primary pr-9 text-xs', inputClassName)}
          value={value}
          onChange={(event) => {
            if (interactionLocked) return
            onChange(event.target.value)
            setOpen(true)
          }}
          onFocus={(e) => {
            if (interactionLocked) {
              if (lockedHint) showAtAnchor(e.currentTarget, lockedHint)
              return
            }
            setOpen(true)
          }}
          onClick={(e) => {
            if (interactionLocked) {
              if (lockedHint) showAtAnchor(e.currentTarget, lockedHint)
              return
            }
            setOpen(true)
          }}
          onPointerDown={(e) => {
            if (interactionLocked && lockedHint) showAtAnchor(e.currentTarget, lockedHint)
          }}
          placeholder={placeholder}
        />
        <button
          type="button"
          className="absolute right-2 top-2.5 text-gray-400"
          onClick={(ev) => {
            if (interactionLocked) {
              if (lockedHint) showAtAnchor(ev.currentTarget, lockedHint)
              return
            }
            setOpen((prev) => !prev)
          }}
        >
          <ChevronDownIcon className={clsx('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </button>
      </div>

      {open && !interactionLocked && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="max-h-52 overflow-y-auto py-1 text-xs">
            {filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onChange(option)
                  setOpen(false)
                }}
                className={clsx(
                  'w-full px-3 py-2 text-left text-xs hover:bg-gray-50',
                  option.toLowerCase() === value.trim().toLowerCase() &&
                    'text-primary-700 font-semibold'
                )}
              >
                {option}
              </button>
            ))}

            {filteredOptions.length === 0 && !showCreate && (
              <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
            )}

            {showCreate && (
              <button
                type="button"
                onClick={() => {
                  const next = value.trim()
                  onChange(next)
                  void (async () => {
                    try {
                      await Promise.resolve(onCreateOption?.(next))
                      setOpen(false)
                    } catch (err: unknown) {
                      const message =
                        err instanceof Error ? err.message : 'Could not add this value. Please try again.'
                      toast.error(message)
                    }
                  })()
                }}
                className={clsx(
                  'w-full px-3 py-2 text-left text-sm text-primary-700 hover:bg-primary-50',
                  filteredOptions.length > 0 && 'border-t border-gray-100'
                )}
              >
                Use “{value.trim()}”
              </button>
            )}

            {!value.trim() && allowCreate && (
              <div className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100">
                Type a new value to add it.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
